/* eslint-disable sort-keys-fix/sort-keys-fix */

/* iceberg-ui */
import {
    Box,
    FadeScroll,
    Flex,
    Text,
    WizardStep
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { useTheme } from '@mui/material/styles';

/* Shared */
import { AuvikASMLogo } from 'application/Shared/Components/App/AuvikLogo';
import {
    INTEGRATION_OPTION_GOOGLE_AUTOMATED,
    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
} from 'application/Shared/Components/Client';

/* React */
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Components */
import { OnboardingGraphic } from './internal/OnboardingGraphic/OnboardingGraphic';
// eslint-disable-next-line no-unused-vars
import { QuickScanChooseGroupsStep } from './internal/QuickScanChooseGroupsStep';
import {
    ChooseOptionStep,
    DeployAgentCompletedStep,
    DeployAgentConfigureStep,
    DeployAgentSelectStep,
    QuickScanCompletedStep,
    QuickScanConfigureStep,
    QuickScanInstallAgentInsteadStep,
    QuickScanSelectStep
} from './internal/index';

const StepHeader = ({ stepNumber, totalSteps }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Flex
            fontWeight={400}
            justifyContent="space-between"
            data-testid="stepHeader"
            mt={-4}
            mb="4%"
            px="4%"
        >
            <Text fontSize={4} color={theme.palette.onboarding.titleTextColor}>
                {t('Onboarding.currentStep', { stepNumber, totalSteps })}
            </Text>
        </Flex>
    );
};

const Footer = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Flex
            alignItems="center"
            data-testid="onboardingFooter"
            flexDirection="column"
            id="onboardingFooter"
            mt="auto"
            my={5}
            px={4}
        >
            <Text fontSize={2} mb={3}>
                {t('Onboarding.footer.preferSalesTeam')}
            </Text>
            <Text
                as="a"
                color={theme.palette.onboarding.buttonPrimary}
                data-testid="DiscoverySetupContactUs"
                fontSize={1}
                href={`mailto:${t('Onboarding.footer.contactUs.salesEmail')}`}
                mt={2}
                style={{ textDecoration: 'none' }}
            >
                {t('Onboarding.footer.contactUs').toUpperCase()}
            </Text>
        </Flex>
    );
};

// These stepNumberMaps are helpers to the Stepper and the Checklist.
// We have three different ones because we have three different paths, the initial/landing one, and
// two others, when the user chooses either QuickScan, or to Deploy the SaaS Agent.

export const stepNumberMapInitial = {
    welcomeScreen: 0,
    tenantCreationLoading: 1,
    chooseOptionStep: 2
};

export const stepNumberMapQuickScan = {
    select: 1,
    configureAD: 2,
    configureGoogle: 2,
    chooseGroups: 3,
    completed: 4
};

export const stepNumberMapDeployAgent = {
    select: 1,
    configure: 2,
    completed: 3
};

export const calculateWizardStatus = (currentStep, stepNumber) => {
    if (currentStep === stepNumber) {
        return 'current';
    }
    if (currentStep > stepNumber) {
        return 'completed';
    }
    return 'pending';
};

export const OnboardingBase = ({ match }) => {
    const theme = useTheme();
    const {
        currentStep = 'chooseOption',
        currentType = 'initial',
        currentSelection
    } = match.params;

    const [linkedTenantId, setLinkedTenantId] = useState(null);

    const [stepNumberMap, setStepNumberMap] = useState(stepNumberMapInitial);

    const [integrationType, setIntegrationType] = useState(null);

    useEffect(() => {
        if (currentStep === 'configureAD') {
            setIntegrationType(INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED);
        } else if (currentStep === 'configureGoogle') {
            setIntegrationType(INTEGRATION_OPTION_GOOGLE_AUTOMATED);
        }
    }, [currentStep]);

    const leftWidth =
        currentType === 'quickScan' || currentType === 'initial'
            ? '50%'
            : '67%';

    const rightWidth =
        currentType === 'quickScan' || currentType === 'initial'
            ? '50%'
            : '33%';

    // When the onboardding starts, we land in the "initial" path, which includes the welcome screen,
    // creating the tenant (both currently living in ANM), and the choose QuickScan or Deploy SaaS Agent screen.
    // In this last screen, the user can take two different paths, either choosing QuickScan (stepComponentMapQuickScan), or
    // deploying the SaaS Agent (stepComponentMapDeployAgent).
    // These stepComponentMaps are meant to differentiate between the initial path and the other two.

    const stepComponentMapInitial = {
        // https://www.figma.com/file/tcLHxZ1uXMrr7zLMtbWkNF/SaaS-Quick-Scan?node-id=356-10650&t=Ks02FmYygsZQEOof-0
        // TODO: Add screen 1a/b: Welcome to ASM screen
        // TODO: Add screen 2: Tenant creation (loading) screen.
        chooseOption: <ChooseOptionStep />
    };

    const stepComponentMapQuickScan = {
        select: <QuickScanSelectStep />,
        configureAD: (
            <QuickScanConfigureStep
                integrationType={
                    INTEGRATION_OPTION_MICROSOFT_ENTRA_ID_AUTOMATED
                }
                linkedTenantId={linkedTenantId}
                setLinkedTenantId={setLinkedTenantId}
            />
        ),
        configureGoogle: (
            <QuickScanConfigureStep
                integrationType={INTEGRATION_OPTION_GOOGLE_AUTOMATED}
                linkedTenantId={linkedTenantId}
                setLinkedTenantId={setLinkedTenantId}
            />
        ),
        chooseGroups: (
            <QuickScanChooseGroupsStep linkedTenantId={linkedTenantId} />
        ),
        completed:
            currentSelection === 'success' ? (
                <QuickScanCompletedStep
                    integrationType={integrationType}
                    clientId={linkedTenantId}
                />
            ) : (
                <QuickScanInstallAgentInsteadStep />
            )
    };

    const stepComponentMapDeployAgent = {
        select: <DeployAgentSelectStep />,
        configure: <DeployAgentConfigureStep />,
        completed: <DeployAgentCompletedStep />
    };

    useLayoutEffect(() => {
        if (currentType === 'quickScan') {
            setStepNumberMap(stepNumberMapQuickScan);
        }
        if (currentType === 'deployAgent') {
            setStepNumberMap(stepNumberMapDeployAgent);
        }
        if (currentType === 'initial') {
            setStepNumberMap(stepNumberMapInitial);
        }
    }, [currentType]);

    useEffect(() => {
        const onboardingBase = document.getElementById('onboardingBaseElement');
        onboardingBase.scrollTo({ top: 0 });
        const fadeScroll = document.getElementById(
            'onboardingFadeScrollElement'
        );
        fadeScroll?.scrollTo({ top: 0 });
    }, [currentStep]);

    const headerHeight = '175px';

    useLayoutEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    return (
        <Flex
            data-testid="onboardingBase"
            flexDirection={['column', 'column', 'row']}
            left={0}
            height="100vh"
            minWidth="818px"
            position="fixed"
            top={0}
            width="100%"
            overflow={['auto', 'auto', 'visible']}
            id="onboardingBaseElement"
        >
            <Flex
                width={['100%', '100%', leftWidth]}
                flexDirection="column"
                bg={theme.palette.onboarding.backgroundColorPrimary}
            >
                <Flex
                    height={headerHeight}
                    flexDirection="column"
                    justifyContent="space-between"
                >
                    {currentType !== 'initial' && ( // Only show stepper if it is not the welcome screens.
                        <Flex
                            bg={theme.palette.onboarding.backgroundColorPrimary}
                            px={3}
                            py={2}
                            height="80px"
                            flexDirection="row"
                            alignItems="center"
                        >
                            {[...new Set(Object.values(stepNumberMap))].map(
                                (value) => (
                                    <WizardStep
                                        key={`wizardStep_${value}`}
                                        align="row"
                                        initialStep={stepNumberMap[currentStep]}
                                        showLabel={false}
                                        showSeparation={
                                            value <
                                            [
                                                ...new Set(
                                                    Object.values(stepNumberMap)
                                                )
                                            ].length
                                        }
                                        status={calculateWizardStatus(
                                            stepNumberMap[currentStep],
                                            value
                                        )}
                                        stepNumber={value}
                                    />
                                )
                            )}
                        </Flex>
                    )}
                    <Box
                        mt={currentType !== 'initial' ? 0 : 5}
                        textAlign="center"
                    >
                        <AuvikASMLogo style={{ width: '100px' }} />
                    </Box>
                    {currentType !== 'initial' && ( // Only show 'Step # of #' legend if it is not the welcome screens.
                        <StepHeader
                            stepNumber={stepNumberMap[currentStep]}
                            totalSteps={
                                [...new Set(Object.values(stepNumberMap))]
                                    .length
                            }
                        />
                    )}
                </Flex>
                <FadeScroll
                    fadePx={5}
                    height={['auto', 'auto', `calc(100vh - ${headerHeight})`]}
                    id="onboardingFadeScrollElement"
                >
                    <Box>
                        {currentType === 'initial' &&
                            stepComponentMapInitial[currentStep]}
                        {currentType === 'quickScan' &&
                            stepComponentMapQuickScan[currentStep]}
                        {currentType === 'deployAgent' &&
                            stepComponentMapDeployAgent[currentStep]}
                        <Footer />
                    </Box>
                </FadeScroll>
            </Flex>
            <Box
                backgroundColor={
                    theme.palette.onboarding.backgroundColorSecondary
                }
                minHeight={['750px', '750px', 0]}
                width={['100%', '100%', rightWidth]}
            >
                <OnboardingGraphic stepNumberMap={stepNumberMap} />
            </Box>
        </Flex>
    );
};
